import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { baseURL } from '../../../api/api';
import { articlesApi, journalsApi, releasesApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const ArticlesList = () => {
    const { toast, confirm } = useUi();
    const list = useAdminList(articlesApi.list, { sort: 'id', dir: 'desc' });
    const { reload } = list;
    const journalId = list.filters.journal_id || '';
    const releaseId = list.filters.release_id || '';

    // Варианты для фильтров «Журнал» и «Выпуск».
    const [journals, setJournals] = useState([]);
    const [releases, setReleases] = useState([]);

    useEffect(() => {
        journalsApi
            .list({ perPage: 200, sort: 'name', dir: 'asc' })
            .then((page) => setJournals(page.items))
            .catch(() => setJournals([]));
    }, []);

    useEffect(() => {
        if (!journalId) {
            setReleases([]);
            return;
        }
        releasesApi
            .list({ journal_id: journalId, perPage: 200, sort: 'date', dir: 'desc' })
            .then((page) => setReleases(page.items))
            .catch(() => setReleases([]));
    }, [journalId]);

    const handleDelete = useCallback(
        async (row) => {
            const ok = await confirm({
                title: 'Удалить статью?',
                message: `«${row.name}»\n\nСтатья и её PDF будут удалены безвозвратно.`,
                confirmLabel: 'Удалить',
                danger: true,
            });
            if (!ok) return;

            try {
                await articlesApi.remove(row.id);
                toast.success('Статья удалена');
                reload();
            } catch (e) {
                toast.error(e.message);
            }
        },
        [confirm, toast, reload]
    );

    const handleVisibility = useCallback(
        async (row) => {
            try {
                await articlesApi.setVisibility(row.id, row.view === 1 ? 0 : 1);
                toast.success(row.view === 1 ? 'Статья скрыта с сайта' : 'Статья опубликована');
                reload();
            } catch (e) {
                toast.error(e.message);
            }
        },
        [toast, reload]
    );

    const columns = [
        { key: 'id', title: 'ID', width: 70, sortable: true },
        {
            key: 'name',
            title: 'Название',
            sortable: true,
            render: (row) => (
                <div>
                    <NavLink to={`/admin/articles/${row.id}`} className={styles.primaryLink}>
                        {row.name || <span className="adm-muted">без названия</span>}
                    </NavLink>
                    {row.authors.length > 0 && (
                        <div className={styles.subline}>{row.authors.map((a) => a.name).join(', ')}</div>
                    )}
                    {row.udk && <div className={styles.subline}>УДК {row.udk}</div>}
                </div>
            ),
        },
        {
            key: 'release',
            title: 'Выпуск',
            width: 200,
            render: (row) =>
                row.release ? (
                    <div>
                        <div>{row.release.journal ? row.release.journal.name : '—'}</div>
                        <div className={styles.subline}>
                            №{row.release.number || '—'}
                            {row.release.tome ? `, том ${row.release.tome}` : ''}
                        </div>
                    </div>
                ) : (
                    <span className="adm-badge adm-badge-warning">не указан</span>
                ),
        },
        {
            key: 'file_path',
            title: 'Файл',
            width: 100,
            render: (row) =>
                row.file_path ? (
                    <a href={baseURL + row.file_path} target="_blank" rel="noopener noreferrer">
                        PDF ↗
                    </a>
                ) : (
                    <span className="adm-badge adm-badge-warning">нет</span>
                ),
        },
        {
            key: 'view',
            title: 'На сайте',
            width: 110,
            render: (row) => (
                <button
                    type="button"
                    className={styles.statusToggle}
                    onClick={() => handleVisibility(row)}
                    title={row.view === 1 ? 'Скрыть с сайта' : 'Опубликовать'}
                >
                    <span className={`adm-badge ${row.view === 1 ? 'adm-badge-success' : 'adm-badge-neutral'}`}>
                        {row.view === 1 ? 'опубликована' : 'скрыта'}
                    </span>
                </button>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <NavLink to={`/admin/articles/${row.id}`} className="adm-btn adm-btn-ghost adm-btn-sm">
                        Изменить
                    </NavLink>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Статьи</h1>
                    <p className="adm-page-subtitle">Публикации научного журнала</p>
                </div>
                <NavLink
                    to={
                        journalId && releaseId
                            ? `/admin/articles/new?journal=${journalId}&release=${releaseId}`
                            : '/admin/articles/new'
                    }
                    className="adm-btn adm-btn-primary"
                >
                    {journalId && releaseId ? '+ Добавить статью в этот выпуск' : '+ Добавить статью'}
                </NavLink>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по названию, УДК, аннотации"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Статей пока нет'}
                emptyHint={list.search ? 'Попробуйте изменить поисковый запрос' : 'Добавьте первую статью'}
                filters={
                    <>
                        <select
                            className={styles.filterSelect}
                            value={journalId}
                            onChange={(e) => list.setFilters({ journal_id: e.target.value, release_id: '' })}
                            aria-label="Фильтр по журналу"
                        >
                            <option value="">Все журналы</option>
                            {journals.map((j) => (
                                <option key={j.id} value={j.id}>
                                    {j.name}
                                </option>
                            ))}
                        </select>
                        <select
                            className={styles.filterSelect}
                            value={releaseId}
                            onChange={(e) => list.setFilter('release_id', e.target.value)}
                            disabled={!journalId}
                            aria-label="Фильтр по выпуску"
                        >
                            <option value="">{journalId ? 'Все выпуски' : 'Выпуск: сначала журнал'}</option>
                            {releases.map((r) => (
                                <option key={r.id} value={r.id}>
                                    №{r.number || '—'}
                                    {r.tome ? `, том ${r.tome}` : ''}
                                    {r.date ? ` (${r.date.slice(0, 4)})` : ''}
                                </option>
                            ))}
                        </select>
                        <select
                            className={styles.filterSelect}
                            value={list.filters.view === undefined ? '' : list.filters.view}
                            onChange={(e) => list.setFilter('view', e.target.value)}
                            aria-label="Фильтр по видимости"
                        >
                            <option value="">Все статьи</option>
                            <option value="1">Только опубликованные</option>
                            <option value="0">Только скрытые</option>
                        </select>
                    </>
                }
            />
        </>
    );
};

export default ArticlesList;
